import React from 'react';

export const PLATFORM_LABELS = {
  facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn',
  twitter: 'X', youtube: 'YouTube', tiktok: 'TikTok',
};

export const PROVIDER_LABELS = { google: 'Google Workspace', microsoft: 'Microsoft 365', other: 'Other mail host' };

export const SITE_PLATFORM_LABELS = {
  wordpress: 'WordPress', wix: 'Wix', squarespace: 'Squarespace', shopify: 'Shopify', weebly: 'Weebly',
  godaddy: 'GoDaddy', webflow: 'Webflow', joomla: 'Joomla', duda: 'Duda', drupal: 'Drupal', jimdo: 'Jimdo',
  site123: 'Site123', strikingly: 'Strikingly', framer: 'Framer', bigcommerce: 'BigCommerce',
  magento: 'Magento', prestashop: 'PrestaShop', ghost: 'Ghost',
};

export const TRACKING_LABELS = {
  google_analytics: 'Google Analytics', google_tag_manager: 'Google Tag Manager', facebook_pixel: 'Meta Pixel',
  tiktok_pixel: 'TikTok Pixel', hotjar: 'Hotjar', google_ads: 'Google Ads',
};

// Normalised facts from enrichment.site / enrichment.domain. Only facts that are actually stored.
export const getSiteDetails = (extra) => {
  const site = extra && extra.site && typeof extra.site === 'object' ? extra.site : {};
  const domain = extra && extra.domain && typeof extra.domain === 'object' ? extra.domain : {};
  const platform = typeof site.platform === 'string' && Object.prototype.hasOwnProperty.call(SITE_PLATFORM_LABELS, site.platform)
    ? SITE_PLATFORM_LABELS[site.platform] : null;
  const year = Number.isInteger(site.copyright_year) ? site.copyright_year : null;
  const tracking = Array.isArray(site.tracking) ? site.tracking : null;
  const age = typeof domain.age_years === 'string' && domain.age_years.trim() !== '' ? Number(domain.age_years) : domain.age_years;
  const ageYears = typeof age === 'number' && Number.isFinite(age) && age >= 0 ? Math.floor(age) : null;
  return {
    platform,
    year,
    stale: site.stale === true,
    tracking,
    trackingLabels: tracking
      ? tracking.filter((t) => Object.prototype.hasOwnProperty.call(TRACKING_LABELS, t)).map((t) => TRACKING_LABELS[t])
      : [],
    mobileFriendly: typeof site.mobile_friendly === 'boolean' ? site.mobile_friendly : null,
    ageYears,
    created: typeof domain.created === 'string' ? domain.created : '',
  };
};

// Small chips from enrichment.site / enrichment.domain. Only shows facts that are actually stored.
export const siteChips = (extra) => {
  const chips = [];
  const d = getSiteDetails(extra);
  if (d.platform) chips.push({ key: 'platform', label: d.platform, title: 'Website platform (detected from the page)' });
  if (d.year) {
    chips.push(d.stale
      ? { key: 'year', label: `Site last updated ${d.year}`, cls: 'badge-warning', title: 'Possibly outdated: the footer copyright year is 3 or more years old' }
      : { key: 'year', label: `Site last updated ${d.year}`, title: 'Copyright year found in the page footer' });
  }
  if (d.tracking && d.tracking.length === 0) {
    chips.push({ key: 'tracking', label: 'No tracking seen', title: 'No analytics or ad tags in the page HTML. Tags loaded later by a cookie banner or script may not show.' });
  }
  if (d.mobileFriendly === false) {
    chips.push({ key: 'mobile', label: 'Not mobile friendly', cls: 'badge-warning', title: 'No mobile viewport tag found on the homepage' });
  }
  if (d.ageYears !== null) {
    chips.push({ key: 'age', label: d.ageYears < 1 ? 'Domain <1y' : `Domain ${d.ageYears}y`, title: `Domain registered ${d.created || ''}`.trim() });
  }
  return chips;
};

// Everything the extras show, as data. Returns null when the buyer has no enrichment object.
export const getProspectExtras = (buyer) => {
  const extra = buyer && buyer.enrichment;
  if (!extra || typeof extra !== 'object') return null;
  const socials = Object.entries(extra.socials || {})
    .filter(([, url]) => typeof url === 'string' && /^https?:\/\//.test(url))
    .map(([platform, url]) => ({ platform, url, label: PLATFORM_LABELS[platform] || platform }));
  return {
    socials,
    provider: PROVIDER_LABELS[extra.email_provider] || null,
    contact: typeof extra.contact_page === 'string' && /^https?:\/\//.test(extra.contact_page) ? extra.contact_page : null,
    guessed: extra.email_guessed === true && !!(buyer.email || '').trim(),
    directory: extra.directory_listing === true,
    socialOnly: extra.social_page_only === true,
    chips: siteChips(extra),
    site: getSiteDetails(extra),
  };
};

// Free extras collected during search. Renders nothing when there is nothing to show.
const ProspectExtras = ({ buyer }) => {
  const x = getProspectExtras(buyer);
  if (!x) return null;
  const { socials, provider, contact, guessed, directory, socialOnly, chips } = x;
  if (socials.length === 0 && !provider && !contact && !guessed && !directory && !socialOnly && chips.length === 0) return null;
  return (
    <div className="prospect-extras">
      {socials.map((s) => (
        <a key={s.platform} href={s.url} target="_blank" rel="noopener noreferrer" className="badge badge-info">
          {s.label}
        </a>
      ))}
      {contact && <a href={contact} target="_blank" rel="noopener noreferrer" className="badge badge-info">Contact page</a>}
      {directory && <span className="badge badge-warning" title="Their listed website is a directory page, not their own site">Directory listing only</span>}
      {socialOnly && <span className="badge badge-warning" title="Their listed website is a social media page, not their own site">Social page only</span>}
      {guessed && <span className="badge" title="Not found on their site; common address pattern. Verify before sending.">Guessed</span>}
      {provider && <span className="badge" title="Email provider (from DNS)">{provider}</span>}
      {chips.map((c) => <span key={c.key} className={`badge ${c.cls || ''}`.trim()} title={c.title}>{c.label}</span>)}
    </div>
  );
};

export default ProspectExtras;
